import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IoCloudDownloadOutline, IoChevronDown } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaUser, FaCheck, FaTrophy } from 'react-icons/fa';
import { IniciativaFormatada } from 'types/iniciativa';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito
} from 'services/iniciativa-action.service';
import { getResultadoLabel } from 'utils/iniciativa-categoria';
import iniciativaVideoImg from 'assets/img/2026/ELBB/iniciativa_video.png';
import './ModalIniciativa.scss';

interface ModalIniciativaProps {
  isOpen: boolean;
  onClose: () => void;
  iniciativa: IniciativaFormatada;
}

export const ModalIniciativa: React.FC<ModalIniciativaProps> = ({
  isOpen,
  onClose,
  iniciativa
}) => {
  const { user } = useAuth();
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(iniciativa.usuarioElogio || '');
  const [isFavorited, setIsFavorited] = useState(iniciativa.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);
  const complimentRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const iniciativaAny = iniciativa as any;
  const documentacaoUrl =
    iniciativaAny?.documentacao ||
    iniciativaAny?.iniciativaOriginal?.documentacao ||
    iniciativaAny?.iniciativaOriginal?.documentacaoComplementar ||
    iniciativaAny?.iniciativaOriginal?.documento?.find((doc: any) => doc?.type === 'file' && doc?.file)?.file;
  const labelResultado = getResultadoLabel(
    iniciativa.categoryPremio,
    iniciativaAny?.iniciativaOriginal?.indicator,
    iniciativaAny?.iniciativaOriginal?.result || iniciativa.texto
  );

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  // Semântica de diálogo acessível: ao abrir, leva o foco para o modal e
  // guarda o elemento previamente focado; ao fechar, devolve o foco a ele.
  // Escape fecha o modal e Tab fica preso dentro dele (focus trap), como manda
  // o padrão WAI-ARIA de dialog — essencial para quem navega por teclado/leitor.
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const modalNode = modalRef.current;
    modalNode?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !modalNode) return;

      const focusables = modalNode.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: iniciativa.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: iniciativa.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = () => {
    if (!documentacaoUrl) return;

    window.open(documentacaoUrl, '_blank', 'noopener,noreferrer');
  };

  const badgeLabel = iniciativa.categoryPremio === 'ELBB'
    ? (iniciativa.perspectiva || iniciativa.tema)
    : iniciativa.categoryPremio;

  const shouldShowMedia = iniciativa.categoryPremio === 'ELBB'
    ? !!iniciativa.videoUrl
    : !!(iniciativa.videoUrl || iniciativa.imagem);

  const descricaoCompleta = iniciativa.iniciativaOriginal?.description || iniciativa.descricao || '';
  const textoCompleto = iniciativa.iniciativaOriginal?.result || iniciativa.texto || '';

  // Renderizado via portal em `document.body` para escapar de qualquer
  // ancestral com `transform`/`filter`/`perspective` (que capturariam o
  // `position: fixed` do overlay e prenderiam o modal dentro do card).
  return ReactDOM.createPortal(
    <div className="modal-iniciativa-overlay" onClick={handleOverlayClick}>
      <div
        className={[
          'modal-iniciativa',
          (iniciativa.classificadaEtapa1 || iniciativa.classificadaEtapa2)
            ? 'modal-iniciativa--classificada'
            : '',
          iniciativa.classificadaEtapa2 ? 'modal-iniciativa--classificada-etapa2' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-iniciativa-title"
        tabIndex={-1}
      >
        {(iniciativa.classificadaEtapa1 || iniciativa.classificadaEtapa2) && (
          <div className="modal-iniciativa__classificada-header">
            <span className="modal-iniciativa__classificada-label" tabIndex={0}>
              <FaTrophy size={24} aria-hidden="true" />
              {iniciativa.classificadaEtapa2
                ? 'CLASSIFICADA ETAPA 2'
                : 'CLASSIFICADA ETAPA 1'}
            </span>
            <div
              className={`modal-iniciativa__badge tag-${iniciativa.corCategoria}`}
              tabIndex={0}
              aria-label={`Categoria: ${badgeLabel}`}
            >
              {badgeLabel}
            </div>
          </div>
        )}
        <div className="modal-iniciativa__body">
          <div className="modal-iniciativa__content-wrapper">
            <div className="modal-iniciativa__left">
              {shouldShowMedia && (
                <div className="modal-iniciativa__video-container">
                  {iniciativa.videoUrl ? (
                    <a
                      href={iniciativa.originalVideoUrl || iniciativa.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modal-iniciativa__video-link"
                      aria-label={`Assistir ao vídeo da iniciativa ${iniciativa.titulo} (abre em nova aba)`}
                    >
                      <img
                        src={iniciativaVideoImg}
                        alt=""
                        aria-hidden="true"
                        className="modal-iniciativa__video-banner"
                      />
                    </a>
                  ) : (
                    <img
                      src={iniciativa.imagem}
                      alt={`Imagem da iniciativa ${iniciativa.titulo}`}
                      className="modal-iniciativa__video-thumbnail"
                      tabIndex={0}
                    />
                  )}
                </div>
              )}

              {documentacaoUrl && (
                <div className="modal-iniciativa__documentacao">
                  <p className="modal-iniciativa__documentacao-label" tabIndex={0}>
                    Documentação complementar da realização
                  </p>
                  <button
                    className="modal-iniciativa__download-btn"
                    onClick={handleDownload}
                    aria-label="Baixar documentação complementar da realização"
                  >
                    <IoCloudDownloadOutline size={20} aria-hidden="true" />
                    <span>DOWNLOAD</span>
                  </button>
                </div>
              )}

              {(iniciativa.elogiosPorTipo.length > 0 || iniciativa.totalFavoritos > 0) && (
                <div className="modal-iniciativa__feedback-box" role="region" aria-label="Feedback na iniciativa">
                  <h3 className="modal-iniciativa__feedback-title" tabIndex={0}>Feedback na iniciativa:</h3>
                  <div className="modal-iniciativa__feedback-list" role="list">
                    {iniciativa.elogiosPorTipo.map((item, index) => (
                      <div key={index} className="modal-iniciativa__feedback-item" role="listitem" tabIndex={0} aria-label={`${item.quantidade} ${item.elogio}`}>
                        <span className="modal-iniciativa__feedback-count" aria-hidden="true">{String(item.quantidade).padStart(2, '0')}</span>
                        <span className="modal-iniciativa__feedback-label" aria-hidden="true">{item.elogio}</span>
                      </div>
                    ))}
                    {iniciativa.totalFavoritos > 0 && (
                      <div className="modal-iniciativa__feedback-item" role="listitem" tabIndex={0} aria-label={`${iniciativa.totalFavoritos} salvos nas favoritas`}>
                        <span className="modal-iniciativa__feedback-count" aria-hidden="true">{String(iniciativa.totalFavoritos).padStart(2, '0')}</span>
                        <span className="modal-iniciativa__feedback-label" aria-hidden="true">Salvos nas favoritas</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-iniciativa__right">
              {!iniciativa.classificadaEtapa1 && !iniciativa.classificadaEtapa2 && (
                <div
                  className={`modal-iniciativa__badge tag-${iniciativa.corCategoria}`}
                  tabIndex={0}
                  aria-label={`Categoria: ${badgeLabel}`}
                >
                  {badgeLabel}
                </div>
              )}
              <h2 className="modal-iniciativa__title" id="modal-iniciativa-title" tabIndex={0}>{iniciativa.titulo}</h2>
              <div className="modal-iniciativa__description">
                <p className="modal-iniciativa__subtitle" tabIndex={0}>
                  {descricaoCompleta}
                </p>

                <div className="modal-iniciativa__info-box" role="region" aria-label="Informações de cadastro">
                  <div className="modal-iniciativa__info-item" tabIndex={0} aria-label={`Cadastrado por: ${iniciativa.cadastradoPor}, matrícula ${iniciativa.cadastradoMatricula}, em ${iniciativa.cadastradoData}`}>
                    <span className="modal-iniciativa__info-label">Cadastrado por:</span>
                    <span className="modal-iniciativa__info-value">
                      {iniciativa.cadastradoPor} ({iniciativa.cadastradoMatricula}), em {iniciativa.cadastradoData}
                    </span>
                  </div>
                  <div className="modal-iniciativa__info-item" tabIndex={0} aria-label={`Inscrito por: ${iniciativa.inscritoPor}, matrícula ${iniciativa.inscritoMatricula}, em ${iniciativa.inscritoData}`}>
                    <span className="modal-iniciativa__info-label">Inscrito por:</span>
                    <span className="modal-iniciativa__info-value">
                      {iniciativa.inscritoPor} ({iniciativa.inscritoMatricula}), em {iniciativa.inscritoData}
                    </span>
                  </div>
                </div>

                {labelResultado.mostrar && !!textoCompleto && (
                  <div className="modal-iniciativa__question">
                    <h3 className="modal-iniciativa__question-title" tabIndex={0}>
                      {labelResultado.titulo}
                    </h3>
                    <p className="modal-iniciativa__question-text" tabIndex={0}>
                      {textoCompleto}
                    </p>
                  </div>
                )}

                <div className="modal-iniciativa__participantes" role="region" aria-label="Participantes da realização">
                  <h3 className="modal-iniciativa__participantes-title" tabIndex={0}>
                    Participantes da realização ({iniciativa.participantes.filter(p => p.confirmado).length} de {iniciativa.participantes.length} confirmados)
                  </h3>
                  <div className="modal-iniciativa__participantes-grid" role="list">
                    {iniciativa.participantes.map((participante, index) => (
                      <div
                        key={index}
                        className={`modal-iniciativa__participante ${
                          participante.confirmado ? 'confirmado' : 'pendente'
                        }`}
                        role="listitem"
                        tabIndex={0}
                        aria-label={`${participante.nome}, matrícula ${participante.matricula}. ${participante.confirmado ? 'Confirmado' : 'Pendente'}`}
                      >
                        <div className="modal-iniciativa__participante-content">
                          <FaUser className="modal-iniciativa__participante-avatar" size={18} aria-hidden="true" />

                          {participante.confirmado && (
                            <FaCheck className="modal-iniciativa__participante-check" size={18} aria-hidden="true" />
                          )}

                          <span className="modal-iniciativa__participante-nome">
                            {participante.nome} <span className="modal-iniciativa__participante-matricula">({participante.matricula})</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-iniciativa__action-bar">
                  <div className="modal-iniciativa__action-left">
                    <div className="modal-iniciativa__icon-wrapper">
                      <BsEmojiSmile size={20} aria-hidden="true" style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }} />
                      {iniciativa.totalElogios > 0 && (
                        <span className="modal-iniciativa__badge-count" aria-label={`${iniciativa.totalElogios} elogios`}>{iniciativa.totalElogios}</span>
                      )}
                    </div>
                    <div className="modal-iniciativa__compliment-container" ref={complimentRef}>
                      <button
                        type="button"
                        className="modal-iniciativa__compliment-dropdown"
                        onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                        aria-expanded={isComplimentOpen}
                        aria-haspopup="listbox"
                        aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}` : 'Faça um elogio'}
                        disabled={loadingAction}
                        style={{
                          opacity: loadingAction ? 0.6 : 1,
                          cursor: loadingAction ? 'not-allowed' : 'pointer',
                          borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                          color: selectedCompliment ? '#5D5FEF' : '#666666'
                        }}
                      >
                        <span>{selectedCompliment || 'Faça um elogio!'}</span>
                        <IoChevronDown size={16} aria-hidden="true" />
                      </button>
                      {isComplimentOpen && !loadingAction && (
                        <div className="modal-iniciativa__compliment-menu" role="listbox" aria-label="Opções de elogio">
                          {complimentOptions.map((compliment) => (
                            <button
                              key={compliment}
                              className="modal-iniciativa__compliment-option"
                              role="option"
                              aria-selected={selectedCompliment === compliment}
                              onClick={() => handleComplimentSelect(compliment)}
                            >
                              {compliment}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-iniciativa__action-right">
                    <div className="modal-iniciativa__icon-wrapper">
                      <button
                        className="modal-iniciativa__action-icon-btn"
                        aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                        aria-pressed={isFavorited}
                        onClick={handleToggleFavorite}
                        disabled={loadingAction}
                        style={{
                          opacity: loadingAction ? 0.6 : 1,
                          color: isFavorited ? '#5D5FEF' : '#666666'
                        }}
                      >
                        {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                      </button>
                      {iniciativa.totalFavoritos > 0 && (
                        <span className="modal-iniciativa__badge-count" aria-label={`${iniciativa.totalFavoritos} favoritos`}>{iniciativa.totalFavoritos}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-iniciativa__footer">
          <button
            className="modal-iniciativa__fechar-btn"
            onClick={onClose}
            aria-label="Fechar detalhes da iniciativa"
          >
            FECHAR
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
